import { useState, useCallback, useEffect } from "react";
import { getEnglishTypeTranslation } from '@/shared/lib/appHelpers';
import { pageSize } from '@/shared/config/constants';
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"
import { useReportsContext } from '@/features/reports/ReportsContext';

function debounce(func, timeout = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

function EmptyFilterMode({ reset, filters }) {
  return reset || Object.values(filters).every((value) => value === "");
}

function useReportFilters() {
  const { currentToolFields, toolName, toolId } = useCurrentTool();
  const { page, setReportsQueryArgs } = useReportsContext();
  
  const getInitialFilters = useCallback(() => {
    const savedFilters = localStorage.getItem(`${toolName} filters`);
    if (savedFilters && savedFilters !== "undefined") {
      return JSON.parse(savedFilters);
    }

    return currentToolFields?.reduce(
      (acc, field) => {
        acc[field.name] = "";
        return acc;
      },
      { comment: "", startDate: "", endDate: "" },
    );
  }, [currentToolFields, toolName]);

  const [appliedFilterValues, setAppliedFilterValues] = useState(() => getInitialFilters());
  const [filterValues, setFilterValues] = useState(() => getInitialFilters());
  const [searchUserName, setSearchUserName] = useState("");

  const handleFilterSearch = useCallback(({ search, reset, resetSearchBar = false, searchUserName, page = 1, filters: filtersInput }) => {
      const filters = reset ? {} : (filtersInput || filterValues || getInitialFilters());
      const userId = localStorage.getItem("user_id");

      if (!userId || !toolId) return;

      search = !search && !resetSearchBar ? searchUserName : search;
      search = search && getEnglishTypeTranslation(search?.trim());

      setReportsQueryArgs({
        userId,
        page,
        pageSize,
        search,
        filters: !EmptyFilterMode({ reset, filters }) ? filters : undefined,
        toolId,
      });
    },
    [filterValues, getInitialFilters, toolId, setReportsQueryArgs]
  );

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (currentToolFields.length === 0 || !userId || !toolId) return;
    const initialFilters = getInitialFilters();
    setFilterValues(initialFilters);
    setAppliedFilterValues(initialFilters);
    handleFilterSearch({filters: initialFilters, page, search: searchUserName});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId, page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleSearchUserName = useCallback(
    debounce(({ search, setPage }) => {
      handleFilterSearch({ search, page: 1 });
      setPage(1);
    }, 500),
    [handleFilterSearch]
  );

  const handleSearchUserName = (e, setPage) => {
    const { value } = e.target;
    setSearchUserName(value);
    debouncedHandleSearchUserName({ search: value, setPage });
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilterValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const resetFilter = (setPage) => {
    if (!currentToolFields) return;
    try {
      setSearchUserName("");

      const emptyFilters = currentToolFields?.reduce(
        (acc, field) => ({
          ...acc,
          [field.name]: "",
        }),
        { comment: "", startDate: "", endDate: "" }
      );
    
      requestAnimationFrame(() => {
        setFilterValues(emptyFilters);
        setAppliedFilterValues(emptyFilters);
        localStorage.removeItem(`${toolName} filters`);
        handleFilterSearch({ search: "", reset: true, page: 1 });
        setPage(1);
      });
    } catch (error) {
      console.error("Error resetting filters:", error);
    }
  };

  const handleFormSubmit = (e, setPage) => {
    e.preventDefault();
    setAppliedFilterValues(filterValues);
    localStorage.setItem(`${toolName} filters`, JSON.stringify(filterValues));
    handleFilterSearch({ page: 1 });
    setPage(1);
  };

  function countFiltersApplied() {
    let filters = 0;
    for (var key in appliedFilterValues) {
      if (appliedFilterValues[key] && appliedFilterValues[key] !== "") {
        filters++;
      }
    }
    return filters === 0 ? null : filters;
  }

  return {
    filterValues,
    searchUserName,
    getInitialFilters,
    setSearchUserName,
    handleFilterSearch,
    handleFilterInputChange,
    resetFilter,
    handleSearchUserName,
    handleFormSubmit,
    countFiltersApplied
  };
}

export default useReportFilters;
